import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'src/components/ui/dialog';

export interface ResponseLocationPoint {
  lat: number;
  lng: number;
  city?: string;
  postal_code?: string;
  /** Region (state) */
  region?: string;
  /** Answer for this response (color-coded on map) */
  answerTitle?: string;
}

export interface ResponseLocationMapModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  points: ResponseLocationPoint[];
}

/** Distinct colors for answer-coded markers (one per unique answer) */
const ANSWER_COLOR_PALETTE = [
  '#2563eb', '#dc2626', '#16a34a', '#ca8a04', '#9333ea', '#0d9488', '#db2777', '#4f46e5',
  '#ea580c', '#059669', '#7c3aed', '#be123c', '#0369a1', '#65a30d', '#c026d3',
];

function formatPointTooltip(point: ResponseLocationPoint): string {
  const parts: string[] = [];
  if (point.answerTitle) parts.push(point.answerTitle);
  if (point.city) parts.push(point.city);
  if (point.region) parts.push(point.region);
  if (point.postal_code) parts.push(point.postal_code);
  return parts.length ? parts.join(' · ') : '';
}

function getColorForAnswer(answerTitle: string, colorMap: Map<string, string>): string {
  if (!colorMap.has(answerTitle)) {
    const idx = colorMap.size % ANSWER_COLOR_PALETTE.length;
    colorMap.set(answerTitle, ANSWER_COLOR_PALETTE[idx]);
  }
  return colorMap.get(answerTitle)!;
}

const DEFAULT_CENTER: [number, number] = [39.8283, -98.5795];
const DEFAULT_ZOOM = 4;
const HEAT_RADIUS = 10;
const HEAT_OPACITY = 0.7;

export const ResponseLocationMapModal = ({
  open,
  onOpenChange,
  title,
  points,
}: ResponseLocationMapModalProps): React.ReactElement => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.CircleMarker[]>([]);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    if (!open) {
      setMapReady(false);
      if (mapInstanceRef.current) {
        markersRef.current.forEach((m) => mapInstanceRef.current?.removeLayer(m));
        markersRef.current = [];
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      return;
    }

    const timer = window.setTimeout(() => {
      if (!mapRef.current || mapInstanceRef.current) return;
      const el = mapRef.current;
      const map = L.map(el, {
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        zoomControl: true,
        scrollWheelZoom: true,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 18,
      }).addTo(map);

      mapInstanceRef.current = map;
      setMapReady(true);
    }, 300);

    return () => {
      window.clearTimeout(timer);
      setMapReady(false);
      if (mapInstanceRef.current) {
        markersRef.current.forEach((m) => mapInstanceRef.current?.removeLayer(m));
        markersRef.current = [];
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [open]);

  const uniqueAnswers = useMemo(() => {
    const seen = new Set<string>();
    const order: string[] = [];
    points.forEach((p) => {
      const a = p.answerTitle?.trim();
      if (a && !seen.has(a)) {
        seen.add(a);
        order.push(a);
      }
    });
    return order;
  }, [points]);

  const legendColorMap = useMemo(() => {
    const m = new Map<string, string>();
    uniqueAnswers.forEach((a) => getColorForAnswer(a, m));
    return m;
  }, [uniqueAnswers]);

  useEffect(() => {
    if (!mapInstanceRef.current || !open || !mapReady) return;

    const map = mapInstanceRef.current;
    const colorMap = new Map<string, string>();

    markersRef.current.forEach((m) => map.removeLayer(m));
    markersRef.current = [];

    const validPoints = points.filter((p) => typeof p.lat === 'number' && typeof p.lng === 'number');

    validPoints.forEach((point) => {
      const answerKey = point.answerTitle?.trim() ?? '';
      const fillColor = answerKey ? getColorForAnswer(answerKey, colorMap) : ANSWER_COLOR_PALETTE[0];
      const borderColor = answerKey ? getColorForAnswer(answerKey, colorMap) : ANSWER_COLOR_PALETTE[0];
      const marker = L.circleMarker([point.lat, point.lng], {
        radius: HEAT_RADIUS,
        fillColor,
        color: borderColor,
        weight: 1.5,
        opacity: 0.95,
        fillOpacity: HEAT_OPACITY,
      });
      const tooltipText = formatPointTooltip(point);
      if (tooltipText) {
        marker.bindTooltip(tooltipText, { permanent: false, direction: 'top' });
      }
      marker.addTo(map);
      markersRef.current.push(marker);
    });
    if (validPoints.length > 0) {
      const group = L.featureGroup(markersRef.current);
      map.fitBounds(group.getBounds(), { padding: [24, 24], maxZoom: 14 });
    } else {
      map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
    }

    map.invalidateSize();
    requestAnimationFrame(() => {
      map.invalidateSize();
    });
  }, [open, points, mapReady]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl p-0 gap-0 overflow-hidden bg-white">
        <DialogHeader className="px-6 pt-4 pb-2 bg-white">
          <DialogTitle>{title} — Response locations</DialogTitle>
        </DialogHeader>
        <div className="px-6 pb-6 bg-white">
          {open && (
            <div
              ref={mapRef}
              className="w-full rounded-lg border border-gray-200 bg-gray-50"
              style={{ height: 400, minHeight: 400 }}
            />
          )}
          {uniqueAnswers.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <span className="text-sm font-medium text-gray-600">Answer:</span>
              {uniqueAnswers.map((answer) => (
                <span key={answer} className="inline-flex items-center gap-1.5 text-sm">
                  <span
                    className="size-3 rounded-full shrink-0"
                    style={{ backgroundColor: legendColorMap.get(answer) ?? ANSWER_COLOR_PALETTE[0] }}
                  />
                  <span className="text-gray-700">{answer}</span>
                </span>
              ))}
            </div>
          )}
          {points.length === 0 && (
            <p className="text-sm text-gray-500 mt-2">No location data available for this question.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
